import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LandingHeader from '../components/LandingHeader';
import SiteFooter from '../components/SiteFooter';
import { getApiBaseUrl } from '../apiBase';
import { normalizeLocale } from '../i18n/languages';
import { changeLanguage as applyLanguage } from '../utils/changeLanguage';

const DOC_META = {
  privacy: { type: 'PrivacyPolicy', titleKey: 'privacyTitle', path: '/privacy' },
  terms: { type: 'TermsOfService', titleKey: 'termsTitle', path: '/terms' },
  offer: { type: 'PublicOffer', titleKey: 'offerTitle', path: '/offer' },
  cookies: { type: 'CookiePolicy', titleKey: 'cookiesTitle', path: '/cookies' },
  refund: { type: 'RefundPolicy', titleKey: 'refundTitle', path: '/refund' },
};

function FallbackArticle({ title, bodyHtml }) {
  return (
    <article
      className="sp-data-card"
      style={{ padding: 28, lineHeight: 1.6 }}
      dangerouslySetInnerHTML={{ __html: `<h1>${title}</h1>${bodyHtml}` }}
    />
  );
}

export default function LegalDocumentPublicPage({ docKey = 'privacy' }) {
  const { t, i18n } = useTranslation('legalDocs');
  const [searchParams, setSearchParams] = useSearchParams();
  const meta = DOC_META[docKey] || DOC_META.privacy;

  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const currentLang = normalizeLocale(i18n.resolvedLanguage || i18n.language || 'en');

  useEffect(() => {
    const urlLang = searchParams.get('lang');
    if (!urlLang) return;
    void applyLanguage(normalizeLocale(urlLang));
    const next = new URLSearchParams(searchParams);
    next.delete('lang');
    next.delete('region');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time bootstrap from mobile/deep links
  }, []);

  const loadDocument = useCallback(async (docType, langCode) => {
    setLoading(true);
    setError('');

    const fetchDoc = async (lang) => {
      const api = getApiBaseUrl();
      const res = await fetch(
        `${api}/legal-documents?type=${encodeURIComponent(docType)}&lang=${encodeURIComponent(lang)}`,
        { headers: { 'Accept-Language': lang } }
      );
      if (!res.ok) return null;
      const data = await res.json();
      const content = (data.content || '').trim();
      if (!content) return null;
      return data;
    };

    try {
      let data = await fetchDoc(langCode);
      if (!data && langCode !== 'en') {
        data = await fetchDoc('en');
      }
      if (!data) throw new Error('not found');
      setDoc(data);
    } catch {
      setDoc(null);
      setError(t('notFound'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadDocument(meta.type, currentLang);
  }, [meta.type, currentLang, loadDocument]);

  const title = doc?.title || t(meta.titleKey);
  const showFallback = !loading && (!doc?.content || error);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--sp-gray-50)' }}>
      <LandingHeader />
      <main className="page-wrapper" style={{ flex: 1, maxWidth: 800, margin: '0 auto', padding: '24px 20px 48px' }}>
        <h1 className="page-h1" style={{ margin: '0 0 20px' }}>{title}</h1>

        {doc && (
          <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
            {t('version', { defaultValue: 'Версия' })}: {doc.version}
            {' · '}
            {t('language')}: {(doc.language || currentLang).toUpperCase()}
            {doc.updatedAt && (
              <>
                {' · '}
                {t('updated')}: {new Date(doc.updatedAt).toLocaleDateString()}
              </>
            )}
          </p>
        )}

        {loading && <p>{t('loading')}</p>}

        {showFallback && (
          <>
            <p style={{ color: '#c62828', marginBottom: 16 }}>{error || t('notFound')}</p>
            <FallbackArticle
              title={title}
              bodyHtml={t('placeholderBody', {
                defaultValue: '<p>Текст документа временно недоступен. Обратитесь в поддержку: <a href="mailto:support@sportprogress.ru">support@sportprogress.ru</a></p>',
              })}
            />
          </>
        )}

        {!loading && doc?.content && (
          <article
            className="sp-data-card legal-doc-body"
            style={{ padding: 28, lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: doc.content }}
          />
        )}

        <nav style={{ marginTop: 32, display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 14 }}>
          <a href="/">{t('backHome', { defaultValue: 'SportProgress' })}</a>
          <a href="/privacy">{t('privacyPolicy')}</a>
          <a href="/terms">{t('termsOfService')}</a>
          <a href="/refund">{t('refundPolicy')}</a>
        </nav>
      </main>
      <SiteFooter />
    </div>
  );
}
